import { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

/**
 * Subscribes to the consultation runtime WebSocket.
 *
 * Returns:
 *  - connected: boolean
 *  - send(payload): emit a JSON payload (object with `type`)
 *  - addEventListener(type, handler): subscribe to a server event type
 *
 * On unmount the socket is closed cleanly. Auto-reconnects up to 5x with
 * exponential backoff if the connection drops while the component is mounted.
 */
export function useConsultationSocket(cid, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const handlersRef = useRef(new Map()); // type -> Set<fn>
  const attemptRef = useRef(0);
  const stoppedRef = useRef(false);

  const dispatch = useCallback((evt) => {
    const set = handlersRef.current.get(evt.type);
    if (!set) return;
    set.forEach((fn) => {
      try { fn(evt); } catch (error) { console.error(`[consultation-ws] handler for "${evt.type}" threw`, error); }
    });
  }, []);

  const connect = useCallback(async () => {
    if (!cid || !enabled) return;
    if (stoppedRef.current) return;
    try {
      const r = await api.post(`/consultations/${cid}/ws-ticket`);
      const ticket = r.data?.ticket;
      if (!ticket) return;
      const wsUrl = API.replace(/^http/, "ws") + `/ws/consultations/${cid}?token=${ticket}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (m) => {
        try {
          const data = JSON.parse(m.data);
          dispatch(data);
        } catch (error) { console.warn("[consultation-ws] dropped non-JSON frame", error?.message || error); }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stoppedRef.current) return;
        const attempt = ++attemptRef.current;
        if (attempt > 5) return;
        setTimeout(connect, Math.min(1000 * Math.pow(2, attempt - 1), 8000));
      };
      ws.onerror = (event) => {
        console.warn("[consultation-ws] socket error", event?.message || "connection error");
        try { ws.close(); } catch { /* already closed */ }
      };
    } catch (error) {
      console.warn("[consultation-ws] connect failed", error?.response?.status || error?.message || error);
      const attempt = ++attemptRef.current;
      if (attempt > 5) return;
      setTimeout(connect, Math.min(1000 * Math.pow(2, attempt - 1), 8000));
    }
  }, [cid, enabled, dispatch]);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      try { wsRef.current?.close(); } catch { /* already closed */ }
    };
  }, [connect]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try { ws.send(JSON.stringify(payload)); return true; } catch { return false; }
  }, []);

  const addEventListener = useCallback((type, fn) => {
    const map = handlersRef.current;
    if (!map.has(type)) map.set(type, new Set());
    map.get(type).add(fn);
    return () => { map.get(type)?.delete(fn); };
  }, []);

  return { connected, send, addEventListener };
}
